import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { GRADES, useAuth } from "../lib/auth.jsx";
import { useI18n } from "../lib/i18n.jsx";
import { toast } from "sonner";
import { ShieldCheck, Edit3, Trash2, Save, X, Check, UserPlus, Upload, FileText, BookOpen, CalendarDays, Plus, Sun } from "lucide-react";

const EMPTY_DRAFT = { name: "", email: "", password: "", grade: "Probationer" };
const EMPTY_PDF = { title: "", description: "", min_grade: "Probationer", file: null };
const EMPTY_EVENT = { name: "", date_label: "", end_date: "" };
const MAX_PDF_BYTES = 10 * 1024 * 1024;

function fmtBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function AdminPage() {
  const { user: me } = useAuth();
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ name: "", email: "" });
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addDraft, setAddDraft] = useState(EMPTY_DRAFT);
  const [addingBusy, setAddingBusy] = useState(false);

  // Library (PDF) upload
  const [pdfDraft, setPdfDraft] = useState(EMPTY_PDF);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [recentUploads, setRecentUploads] = useState([]);

  // Events
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventDraft, setEventDraft] = useState(EMPTY_EVENT);
  const [eventBusy, setEventBusy] = useState(false);
  const [eventAddOpen, setEventAddOpen] = useState(false);
  const [eventEditingId, setEventEditingId] = useState(null);
  const [eventEdit, setEventEdit] = useState(EMPTY_EVENT);

  // Tarot personality card catalog (for name lookup)
  const [personalityCards, setPersonalityCards] = useState({});
  const [eventConfirmRemoveId, setEventConfirmRemoveId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/events");
        setEvents(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load events");
      } finally {
        setEventsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/users");
        setUsers(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load users");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/tarot/personality-cards");
        const map = {};
        data.forEach((c) => {
          map[c.slug] = c;
        });
        setPersonalityCards(map);
      } catch {
        // Non-fatal for admin — leave the map empty; UI will hide the label.
      }
    })();
  }, []);

  const changeGrade = async (id, grade) => {
    setSavingId(id);
    try {
      const { data } = await api.put(`/admin/users/${id}/grade`, { grade });
      setUsers((cur) => cur.map((u) => (u.id === id ? data : u)));
      toast.success(t("admin.saved", { grade }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    } finally {
      setSavingId(null);
    }
  };

  const toggleTarotCourse = async (u, completed) => {
    setSavingId(u.id);
    try {
      const { data } = await api.put(`/admin/users/${u.id}/tarot-course`, { completed });
      setUsers((cur) => cur.map((x) => (x.id === u.id ? data : x)));
      toast.success(
        t(completed ? "admin.tarot_course_saved_on" : "admin.tarot_course_saved_off", { name: data.name })
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    } finally {
      setSavingId(null);
    }
  };

  const startEdit = (u) => {
    setEditingId(u.id);
    setDraft({ name: u.name, email: u.email, new_password: "" });
    setConfirmRemoveId(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft({ name: "", email: "", new_password: "" });
  };

  const saveEdit = async (id) => {
    const newPwd = (draft.new_password || "").trim();
    if (newPwd && newPwd.length < 6) {
      toast.error(t("admin.password_too_short"));
      return;
    }
    setSavingId(id);
    try {
      const body = { name: draft.name, email: draft.email };
      if (newPwd) body.new_password = newPwd;
      const { data } = await api.put(`/admin/users/${id}`, body);
      setUsers((cur) => cur.map((u) => (u.id === id ? data : u)));
      toast.success(newPwd ? t("admin.saved_user_with_pwd") : t("admin.saved_user"));
      cancelEdit();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    } finally {
      setSavingId(null);
    }
  };

  const remove = async (u) => {
    setSavingId(u.id);
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers((cur) => cur.filter((x) => x.id !== u.id));
      toast.success(t("admin.remove_done", { name: u.name }));
      setConfirmRemoveId(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Removal failed");
    } finally {
      setSavingId(null);
    }
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    setAddingBusy(true);
    try {
      const { data } = await api.post("/admin/users", addDraft);
      setUsers((cur) => [data, ...cur]);
      toast.success(t("admin.added_toast", { name: data.name }));
      setAddDraft(EMPTY_DRAFT);
      setAddOpen(false);
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Failed to add");
    } finally {
      setAddingBusy(false);
    }
  };

  const onPdfFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== "application/pdf" && !f.name.toLowerCase().endsWith(".pdf")) {
      toast.error(t("library.not_pdf"));
      e.target.value = "";
      return;
    }
    if (f.size > MAX_PDF_BYTES) {
      toast.error(t("library.too_large"));
      e.target.value = "";
      return;
    }
    setPdfDraft((d) => ({ ...d, file: f }));
  };

  const submitPdf = async (e) => {
    e.preventDefault();
    if (!pdfDraft.file || !pdfDraft.title.trim()) return;
    setPdfBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", pdfDraft.file);
      fd.append("title", pdfDraft.title.trim());
      fd.append("description", pdfDraft.description.trim());
      fd.append("min_grade", pdfDraft.min_grade);
      const { data } = await api.post("/library", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRecentUploads((cur) => [data, ...cur]);
      toast.success(t("library.uploaded_toast"));
      setPdfDraft(EMPTY_PDF);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setPdfBusy(false);
    }
  };

  // ---------- Events ----------
  const submitEvent = async (e) => {
    e.preventDefault();
    if (!eventDraft.name.trim() || !eventDraft.date_label.trim() || !eventDraft.end_date) return;
    setEventBusy(true);
    try {
      const { data } = await api.post("/admin/events", eventDraft);
      setEvents((cur) => [...cur, data].sort((a, b) => a.end_date.localeCompare(b.end_date)));
      toast.success(t("events.created_toast"));
      setEventDraft(EMPTY_EVENT);
      setEventAddOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to add event");
    } finally {
      setEventBusy(false);
    }
  };

  const startEventEdit = (ev) => {
    setEventEditingId(ev.id);
    setEventEdit({ name: ev.name, date_label: ev.date_label, end_date: ev.end_date });
    setEventConfirmRemoveId(null);
  };

  const cancelEventEdit = () => {
    setEventEditingId(null);
    setEventEdit(EMPTY_EVENT);
  };

  const saveEventEdit = async (id) => {
    setEventBusy(true);
    try {
      const { data } = await api.put(`/admin/events/${id}`, eventEdit);
      setEvents((cur) => cur.map((e) => (e.id === id ? data : e)).sort((a, b) => a.end_date.localeCompare(b.end_date)));
      toast.success(t("events.saved_toast"));
      cancelEventEdit();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally {
      setEventBusy(false);
    }
  };

  const removeEvent = async (id) => {
    setEventBusy(true);
    try {
      await api.delete(`/admin/events/${id}`);
      setEvents((cur) => cur.filter((e) => e.id !== id));
      toast.success(t("events.removed_toast"));
      setEventConfirmRemoveId(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Removal failed");
    } finally {
      setEventBusy(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <main data-testid="admin-page" className="max-w-6xl mx-auto px-6 md:px-10 py-12 md:py-16">
      <header className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <p className="label-small text-[#8B3A3A] mb-3 flex items-center gap-2"><ShieldCheck size={12} /> {t("admin.eyebrow")}</p>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tight text-[#1A1918]">{t("admin.heading")}</h1>
          <p className="text-[#4A4238] mt-3 max-w-2xl">{t("admin.intro")}</p>
        </div>
        <button
          data-testid="admin-open-add-btn"
          type="button"
          onClick={() => setAddOpen((v) => !v)}
          className="btn-primary inline-flex items-center gap-2 self-start select-none"
        >
          <UserPlus size={14} /> {t("admin.add_btn")}
        </button>
      </header>

      {addOpen && (
        <form
          onSubmit={submitAdd}
          className="border border-[#C1B49A] bg-[#E0D2B4]/40 p-6 md:p-8 mb-8 space-y-5"
          data-testid="admin-add-form"
        >
          <p className="label-small text-[#8B3A3A] mb-2 flex items-center gap-2"><UserPlus size={12} /> {t("admin.add_heading")}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            <div>
              <label className="label-small block mb-2">{t("admin.field_name")}</label>
              <input data-testid="admin-add-name" type="text" required value={addDraft.name} onChange={(e) => setAddDraft((d) => ({ ...d, name: e.target.value }))} className="input-etched" />
            </div>
            <div>
              <label className="label-small block mb-2">{t("admin.field_email")}</label>
              <input data-testid="admin-add-email" type="email" required value={addDraft.email} onChange={(e) => setAddDraft((d) => ({ ...d, email: e.target.value }))} className="input-etched" />
            </div>
            <div>
              <label className="label-small block mb-2">{t("admin.field_password")}</label>
              <input data-testid="admin-add-password" type="password" required minLength={6} value={addDraft.password} onChange={(e) => setAddDraft((d) => ({ ...d, password: e.target.value }))} className="input-etched" />
            </div>
            <div>
              <label className="label-small block mb-2">{t("admin.col_grade")}</label>
              <select
                data-testid="admin-add-grade"
                value={addDraft.grade}
                onChange={(e) => setAddDraft((d) => ({ ...d, grade: e.target.value }))}
                className="bg-[#F4EEDF] border border-[#1A1918] px-3 py-2 text-xs uppercase tracking-[0.2em] font-semibold rounded-none focus:outline-none focus:border-[#8B3A3A] w-full"
              >
                {GRADES.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={() => { setAddOpen(false); setAddDraft(EMPTY_DRAFT); }} className="btn-secondary inline-flex items-center gap-1.5 select-none">
              <X size={12} /> {t("admin.cancel")}
            </button>
            <button data-testid="admin-add-submit-btn" type="submit" disabled={addingBusy} className="btn-primary inline-flex items-center gap-1.5 select-none">
              <Save size={12} /> {addingBusy ? t("admin.submitting_add") : t("admin.submit_add")}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-[#7A7061] italic">{t("admin.loading")}</p>
      ) : (
        <div className="border border-[#C1B49A]" data-testid="admin-users-table">
          {users.map((u) => {
            const isEditing = editingId === u.id;
            const isSaving = savingId === u.id;
            const isSelf = me?.id === u.id;
            const askingRemove = confirmRemoveId === u.id;
            return (
              <div
                key={u.id}
                data-testid={`admin-user-row-${u.email}`}
                className="grid grid-cols-12 gap-x-4 gap-y-3 px-6 py-5 items-center border-t border-[#C1B49A] first:border-t-0 bg-[#EAE0CA]/30 hover:bg-[#EAE0CA]/60 transition-colors"
              >
                {isEditing ? (
                  <>
                    <div className="col-span-12 md:col-span-5 space-y-2">
                      <label className="label-small block">{t("admin.field_name")}</label>
                      <input
                        data-testid={`admin-edit-name-${u.email}`}
                        type="text"
                        value={draft.name}
                        onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                        className="input-etched"
                      />
                    </div>
                    <div className="col-span-12 md:col-span-4 space-y-2">
                      <label className="label-small block">{t("admin.field_email")}</label>
                      <input
                        data-testid={`admin-edit-email-${u.email}`}
                        type="email"
                        value={draft.email}
                        onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
                        className="input-etched"
                      />
                    </div>
                    <div className="col-span-12 md:col-span-3 flex justify-end gap-2 mt-2 md:mt-6">
                      <button
                        data-testid={`admin-save-edit-${u.email}`}
                        type="button"
                        onClick={() => saveEdit(u.id)}
                        disabled={isSaving}
                        className="btn-primary inline-flex items-center gap-1.5 select-none whitespace-nowrap"
                      >
                        <Save size={12} /> {t("common.save")}
                      </button>
                      <button
                        data-testid={`admin-cancel-edit-${u.email}`}
                        type="button"
                        onClick={cancelEdit}
                        disabled={isSaving}
                        className="btn-secondary inline-flex items-center gap-1.5 select-none whitespace-nowrap"
                      >
                        <X size={12} /> {t("admin.cancel")}
                      </button>
                    </div>
                    <div className="col-span-12 md:col-span-9 space-y-2">
                      <label className="label-small block flex items-center gap-2">
                        {t("admin.field_new_password")}
                        <span className="text-[10px] tracking-normal normal-case italic text-[#7A7061] font-normal">
                          {t("admin.field_new_password_hint")}
                        </span>
                      </label>
                      <input
                        data-testid={`admin-edit-new-password-${u.email}`}
                        type="password"
                        autoComplete="new-password"
                        minLength={6}
                        value={draft.new_password || ""}
                        onChange={(e) => setDraft((d) => ({ ...d, new_password: e.target.value }))}
                        className="input-etched"
                        placeholder={t("admin.field_new_password_placeholder")}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="col-span-12 md:col-span-3">
                      <div className="font-display text-xl text-[#1A1918] leading-tight">{u.name}</div>
                      <div className="text-xs text-[#7A7061] font-mono">{u.id.slice(0, 8)}</div>
                    </div>
                    <div className="col-span-7 md:col-span-3 text-sm text-[#4A4238] break-all">{u.email}</div>
                    <div className="col-span-5 md:col-span-1">
                      {u.role === "admin" ? (
                        <span className="grade-pill" style={{ borderColor: "#8B3A3A", color: "#8B3A3A" }}>{t("admin.role_admin")}</span>
                      ) : (
                        <span className="grade-pill">{t("admin.role_member")}</span>
                      )}
                    </div>
                    <div className="col-span-12 md:col-span-2 flex">
                      <select
                        data-testid={`admin-grade-select-${u.email}`}
                        value={u.grade}
                        disabled={isSaving || u.role === "admin"}
                        onChange={(e) => changeGrade(u.id, e.target.value)}
                        className="bg-[#F4EEDF] border border-[#1A1918] px-2 py-2 text-[10px] tracking-[0.16em] uppercase font-semibold rounded-none focus:outline-none focus:border-[#8B3A3A] w-full"
                      >
                        {GRADES.map((g) => (
                          <option key={g} value={g}>{g}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-12 md:col-span-3 flex md:justify-end gap-2 flex-wrap">
                      <button
                        data-testid={`admin-edit-btn-${u.email}`}
                        type="button"
                        onClick={() => startEdit(u)}
                        disabled={isSaving}
                        className="btn-ghost inline-flex items-center gap-1.5 select-none whitespace-nowrap"
                        title={t("admin.edit_btn")}
                      >
                        <Edit3 size={12} /> {t("admin.edit_btn")}
                      </button>
                      {!isSelf && (
                        askingRemove ? (
                          <div className="flex items-center gap-1">
                            <button
                              data-testid={`admin-confirm-remove-${u.email}`}
                              type="button"
                              onClick={() => remove(u)}
                              disabled={isSaving}
                              className="btn-primary inline-flex items-center gap-1.5 select-none whitespace-nowrap"
                              style={{ padding: "8px 12px", fontSize: "11px" }}
                            >
                              <Check size={12} /> {t("admin.confirm_remove")}
                            </button>
                            <button
                              data-testid={`admin-cancel-remove-${u.email}`}
                              type="button"
                              onClick={() => setConfirmRemoveId(null)}
                              disabled={isSaving}
                              className="btn-ghost select-none"
                              style={{ padding: "8px 8px" }}
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ) : (
                          <button
                            data-testid={`admin-remove-btn-${u.email}`}
                            type="button"
                            onClick={() => setConfirmRemoveId(u.id)}
                            disabled={isSaving}
                            className="btn-ghost inline-flex items-center gap-1.5 text-[#8B3A3A] select-none whitespace-nowrap"
                            title={t("admin.remove_btn")}
                          >
                            <Trash2 size={12} /> {t("admin.remove_btn")}
                          </button>
                        )
                      )}
                    </div>
                    <div className="col-span-12 -mt-1 flex items-center gap-3 pt-2 border-t border-dashed border-[#C1B49A]/70">
                      <Sun size={12} className="text-[#8B3A3A] shrink-0" />
                      <label
                        htmlFor={`admin-tarot-course-${u.email}`}
                        className="label-small text-[#4A4238] font-semibold cursor-pointer select-none"
                      >
                        {t("admin.col_tarot_course")}
                      </label>
                      <input
                        id={`admin-tarot-course-${u.email}`}
                        data-testid={`admin-tarot-course-toggle-${u.email}`}
                        type="checkbox"
                        checked={!!u.tarot_course_completed}
                        disabled={isSaving}
                        onChange={(e) => toggleTarotCourse(u, e.target.checked)}
                        className="h-4 w-4 accent-[#8B3A3A] cursor-pointer"
                      />
                      <span
                        data-testid={`admin-tarot-course-status-${u.email}`}
                        className={`text-[10px] tracking-[0.22em] uppercase font-semibold ${
                          u.tarot_course_completed ? "text-[#8B3A3A]" : "text-[#7A7061]"
                        }`}
                      >
                        {u.tarot_course_completed ? t("admin.tarot_course_done") : t("admin.tarot_course_todo")}
                      </span>
                      <span
                        data-testid={`admin-celtic-count-${u.email}`}
                        className="text-[10px] font-mono tracking-[0.18em] uppercase text-[#4A4238] ml-3"
                        title={t("admin.celtic_readings_hint")}
                      >
                        {t("admin.celtic_readings_label")}: {u.celtic_cross_readings_count ?? 0}
                      </span>
                      {u.tarot_personality?.card_slug && (
                        <span
                          data-testid={`admin-tarot-personality-${u.email}`}
                          className="text-[10px] font-mono tracking-[0.18em] uppercase text-[#4A4238] ml-3"
                          title={t("admin.tarot_personality_hint")}
                        >
                          {t("admin.tarot_personality_label")}:{" "}
                          <span className="text-[#8B3A3A] font-semibold">
                            {personalityCards[u.tarot_personality.card_slug]?.name_nl
                              || personalityCards[u.tarot_personality.card_slug]?.name_en
                              || u.tarot_personality.card_slug}
                          </span>
                        </span>
                      )}
                      <span className="hidden md:inline text-[10px] italic text-[#7A7061] ml-auto">
                        {t("admin.tarot_course_toggle_hint")}
                      </span>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ---------- Library upload section ---------- */}
      <section data-testid="admin-library-section" className="mt-16">
        <header className="mb-6">
          <p className="label-small text-[#8B3A3A] mb-3 flex items-center gap-2"><BookOpen size={12} /> {t("library.eyebrow")}</p>
          <h2 className="font-display text-4xl tracking-tight text-[#1A1918]">{t("library.admin_section_heading")}</h2>
          <p className="text-[#4A4238] mt-2 max-w-2xl text-sm">{t("library.admin_section_intro")}</p>
        </header>

        <form
          onSubmit={submitPdf}
          className="border border-[#C1B49A] bg-[#E0D2B4]/40 p-6 md:p-8 space-y-5"
          data-testid="admin-library-upload-form"
        >
          <p className="label-small text-[#8B3A3A] mb-2 flex items-center gap-2"><Upload size={12} /> {t("library.upload_heading")}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            <div className="md:col-span-2">
              <label className="label-small block mb-2">{t("library.field_title")}</label>
              <input
                data-testid="admin-library-title-input"
                required
                value={pdfDraft.title}
                onChange={(e) => setPdfDraft((d) => ({ ...d, title: e.target.value }))}
                className="input-etched"
              />
            </div>
            <div className="md:col-span-2">
              <label className="label-small block mb-2">{t("library.field_description")}</label>
              <textarea
                data-testid="admin-library-description-input"
                rows={3}
                value={pdfDraft.description}
                onChange={(e) => setPdfDraft((d) => ({ ...d, description: e.target.value }))}
                className="textarea-parchment"
              />
            </div>
            <div>
              <label className="label-small block mb-2">{t("library.field_file")}</label>
              <div className="flex items-center gap-4">
                <label className="btn-secondary inline-flex items-center gap-2 cursor-pointer select-none">
                  <Upload size={12} />
                  <span>{pdfDraft.file ? t("library.change_file") : t("library.choose_file")}</span>
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={onPdfFile}
                    className="hidden"
                    data-testid="admin-library-file-input"
                  />
                </label>
                <span className="text-xs text-[#4A4238] italic break-all" data-testid="admin-library-file-name">
                  {pdfDraft.file ? `${pdfDraft.file.name} · ${fmtBytes(pdfDraft.file.size)}` : t("library.no_file")}
                </span>
              </div>
            </div>
            <div>
              <label className="label-small block mb-2">{t("library.field_min_grade")}</label>
              <select
                data-testid="admin-library-min-grade-select"
                value={pdfDraft.min_grade}
                onChange={(e) => setPdfDraft((d) => ({ ...d, min_grade: e.target.value }))}
                className="bg-[#F4EEDF] border border-[#1A1918] px-3 py-2 text-xs uppercase tracking-[0.2em] font-semibold rounded-none focus:outline-none focus:border-[#8B3A3A] w-full"
              >
                {GRADES.map((g) => (<option key={g} value={g}>{g}</option>))}
              </select>
            </div>
          </div>
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              data-testid="admin-library-submit-btn"
              type="submit"
              disabled={pdfBusy || !pdfDraft.file || !pdfDraft.title.trim()}
              className="btn-primary inline-flex items-center gap-1.5 select-none"
            >
              <Save size={12} /> {pdfBusy ? t("library.submitting") : t("library.submit")}
            </button>
          </div>
        </form>

        {recentUploads.length > 0 && (
          <div className="mt-6 border border-[#C1B49A]" data-testid="admin-library-recent">
            <p className="label-small text-[#7A7061] px-5 py-3 border-b border-[#C1B49A] bg-[#EAE0CA]/60">
              {t("library.recent_uploads")}
            </p>
            <ol>
              {recentUploads.map((it) => (
                <li
                  key={it.id}
                  data-testid={`admin-library-recent-${it.id}`}
                  className="px-5 py-4 border-b border-[#C1B49A] last:border-b-0 flex items-center gap-3 bg-[#EAE0CA]/30"
                >
                  <FileText size={16} className="text-[#8B3A3A] shrink-0" />
                  <span className="font-display text-lg text-[#1A1918] flex-1">{it.title}</span>
                  <span className="text-[10px] tracking-[0.22em] uppercase text-[#8B3A3A] font-semibold">{it.min_grade}+</span>
                  <span className="text-[10px] tracking-[0.18em] uppercase text-[#7A7061] font-mono">{fmtBytes(it.size)}</span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </section>

      {/* ---------- Events section ---------- */}
      <section data-testid="admin-events-section" className="mt-16">
        <header className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="label-small text-[#8B3A3A] mb-3 flex items-center gap-2"><CalendarDays size={12} /> {t("events.eyebrow")}</p>
            <h2 className="font-display text-4xl tracking-tight text-[#1A1918]">{t("events.admin_section_heading")}</h2>
            <p className="text-[#4A4238] mt-2 max-w-2xl text-sm">{t("events.admin_section_intro")}</p>
          </div>
          <button
            data-testid="admin-event-open-add-btn"
            type="button"
            onClick={() => setEventAddOpen((v) => !v)}
            className="btn-primary inline-flex items-center gap-2 self-start select-none"
          >
            <Plus size={14} /> {t("events.add_btn")}
          </button>
        </header>

        {eventAddOpen && (
          <form
            onSubmit={submitEvent}
            className="border border-[#C1B49A] bg-[#E0D2B4]/40 p-6 md:p-8 mb-8 space-y-5"
            data-testid="admin-event-add-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
              <div className="md:col-span-2">
                <label className="label-small block mb-2">{t("events.field_name")}</label>
                <input
                  data-testid="admin-event-name-input"
                  required
                  value={eventDraft.name}
                  onChange={(e) => setEventDraft((d) => ({ ...d, name: e.target.value }))}
                  className="input-etched"
                />
              </div>
              <div>
                <label className="label-small block mb-2">{t("events.field_date_label")}</label>
                <input
                  data-testid="admin-event-date-label-input"
                  required
                  value={eventDraft.date_label}
                  onChange={(e) => setEventDraft((d) => ({ ...d, date_label: e.target.value }))}
                  className="input-etched"
                  placeholder="e.g. 5 July 2026 — or 2,3,4 & 9,10,11 Oct 2026"
                />
              </div>
              <div>
                <label className="label-small block mb-2">{t("events.field_end_date")}</label>
                <input
                  type="date"
                  data-testid="admin-event-end-date-input"
                  required
                  value={eventDraft.end_date}
                  onChange={(e) => setEventDraft((d) => ({ ...d, end_date: e.target.value }))}
                  className="input-etched"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 pt-2">
              <button type="button" onClick={() => { setEventAddOpen(false); setEventDraft(EMPTY_EVENT); }} className="btn-secondary inline-flex items-center gap-1.5 select-none">
                <X size={12} /> {t("events.cancel")}
              </button>
              <button data-testid="admin-event-submit-btn" type="submit" disabled={eventBusy || !eventDraft.name.trim() || !eventDraft.date_label.trim() || !eventDraft.end_date} className="btn-primary inline-flex items-center gap-1.5 select-none">
                <Save size={12} /> {eventBusy ? t("events.submitting") : t("events.submit")}
              </button>
            </div>
          </form>
        )}

        {eventsLoading ? (
          <p className="text-[#7A7061] italic">{t("admin.loading")}</p>
        ) : events.length === 0 ? (
          <div className="border border-dashed border-[#C1B49A] p-10 text-center" data-testid="admin-events-empty">
            <p className="text-sm italic text-[#7A7061]">{t("events.empty")}</p>
          </div>
        ) : (
          <ol className="border border-[#C1B49A]" data-testid="admin-events-list">
            {events.map((ev) => {
              const isEditing = eventEditingId === ev.id;
              const askingRemove = eventConfirmRemoveId === ev.id;
              const isPast = ev.end_date < today;
              return (
                <li
                  key={ev.id}
                  data-testid={`admin-event-row-${ev.id}`}
                  className={`border-b border-[#C1B49A] last:border-b-0 px-6 py-5 ${isPast ? "bg-[#EAE0CA]/20 opacity-60" : "bg-[#EAE0CA]/40"} transition-colors`}
                >
                  {isEditing ? (
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                      <div className="md:col-span-5">
                        <label className="label-small block mb-2">{t("events.field_name")}</label>
                        <input data-testid={`admin-event-edit-name-${ev.id}`} className="input-etched" value={eventEdit.name} onChange={(e) => setEventEdit((d) => ({ ...d, name: e.target.value }))} />
                      </div>
                      <div className="md:col-span-4">
                        <label className="label-small block mb-2">{t("events.field_date_label")}</label>
                        <input data-testid={`admin-event-edit-date-label-${ev.id}`} className="input-etched" value={eventEdit.date_label} onChange={(e) => setEventEdit((d) => ({ ...d, date_label: e.target.value }))} />
                      </div>
                      <div className="md:col-span-3">
                        <label className="label-small block mb-2">{t("events.field_end_date")}</label>
                        <input type="date" data-testid={`admin-event-edit-end-date-${ev.id}`} className="input-etched" value={eventEdit.end_date} onChange={(e) => setEventEdit((d) => ({ ...d, end_date: e.target.value }))} />
                      </div>
                      <div className="md:col-span-12 flex justify-end gap-2">
                        <button type="button" onClick={cancelEventEdit} className="btn-secondary inline-flex items-center gap-1.5 select-none"><X size={12} /> {t("events.cancel")}</button>
                        <button data-testid={`admin-event-save-${ev.id}`} type="button" onClick={() => saveEventEdit(ev.id)} disabled={eventBusy} className="btn-primary inline-flex items-center gap-1.5 select-none">
                          <Save size={12} /> {t("events.save_btn")}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-12 gap-4 items-center">
                      <div className="col-span-12 md:col-span-7">
                        <h3 className="font-display text-2xl text-[#1A1918] leading-tight">{ev.name}</h3>
                        <p className="text-[10px] tracking-[0.22em] uppercase text-[#8B3A3A] font-mono font-semibold mt-1">{ev.date_label}</p>
                        <p className="text-[10px] tracking-[0.18em] uppercase text-[#7A7061] font-mono mt-1">
                          end: {ev.end_date}{isPast && <span className="ml-2 text-[#8B3A3A]">· {t("events.hidden_label")}</span>}
                        </p>
                      </div>
                      <div className="col-span-12 md:col-span-5 flex md:justify-end gap-2 flex-wrap">
                        <button data-testid={`admin-event-edit-${ev.id}`} type="button" onClick={() => startEventEdit(ev)} className="btn-ghost inline-flex items-center gap-1.5 select-none whitespace-nowrap">
                          <Edit3 size={12} /> {t("events.edit_btn")}
                        </button>
                        {askingRemove ? (
                          <div className="flex items-center gap-1">
                            <button
                              data-testid={`admin-event-confirm-remove-${ev.id}`}
                              type="button"
                              onClick={() => removeEvent(ev.id)}
                              disabled={eventBusy}
                              className="btn-primary inline-flex items-center gap-1.5 select-none"
                              style={{ padding: "8px 12px", fontSize: "11px" }}
                            >
                              <Check size={12} /> {t("events.confirm_remove")}
                            </button>
                            <button
                              data-testid={`admin-event-cancel-remove-${ev.id}`}
                              type="button"
                              onClick={() => setEventConfirmRemoveId(null)}
                              className="btn-ghost select-none"
                              style={{ padding: "8px 8px" }}
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ) : (
                          <button data-testid={`admin-event-remove-${ev.id}`} type="button" onClick={() => setEventConfirmRemoveId(ev.id)} className="btn-ghost inline-flex items-center gap-1.5 text-[#8B3A3A] select-none whitespace-nowrap">
                            <Trash2 size={12} /> {t("events.remove_btn")}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </section>
    </main>
  );
}
